/**
 * RebookReceiptPanel — what the rebook drawer becomes once the move has
 * committed (BON-146).
 *
 * There is no confirmation step and no undo (decision 8), so this is the first
 * and only place the CSR sees what actually happened. It leads with a link to
 * the new booking, because the very next thing anyone does after moving a
 * customer is look at where they went — and then shows each field that changed
 * as old → new, so the CSR can read the move back to the customer on the phone
 * without navigating anywhere.
 *
 * `emailSent: false` is NOT an error state. The move committed and cannot be
 * rolled back; the customer simply has not been told, and the successor has been
 * flagged for review. Reporting that as a failure would invite someone to "fix"
 * it by moving them again.
 */
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { BookingDetail } from '../../api/bookingsV2';
import type { RebookBookingResult } from '../../api/rebooking';

import { customerDisplayName } from './bookingDetailModel';
import { formatPickerDate, formatPickerTime } from './shared/WorkshopPickerHelpers';

function whenLabel(local: string | null): string {
  const date = formatPickerDate(local);
  const time = formatPickerTime(local);
  return time ? `${date} · ${time}` : date;
}

function changeRow(label: string, from: string, to: string) {
  return (
    <div
      role="status"
      aria-live="polite"
      key={label}
      className="border-t border-stone-200 px-3.5 py-2.5 first:border-t-0 dark:border-stone-700"
    >
      <div
        role="status"
        aria-live="polite"
        className="text-[10px] font-extrabold uppercase tracking-wider text-stone-500 dark:text-stone-400"
      >
        {label}
      </div>
      <div role="status" aria-live="polite" className="mt-1 flex flex-wrap items-center gap-2">
        <span className="text-xs text-stone-500 line-through dark:text-stone-400">{from}</span>
        <ChevronRight size={12} className="flex-shrink-0 text-stone-400" />
        <span className="text-xs font-semibold text-bark-900 dark:text-stone-100">{to}</span>
      </div>
    </div>
  );
}

export interface RebookReceiptPanelProps {
  booking: BookingDetail;
  result: RebookBookingResult;
  onDone: () => void;
}

export function RebookReceiptPanel({ booking, result, onDone }: RebookReceiptPanelProps) {
  const { newBooking } = result;
  const from = booking.workshop;
  const changes: [string, string, string][] = [
    ['Workshop', from.name ?? 'Workshop', newBooking.workshopName],
    ['Venue', from.venue.name, newBooking.venueName],
    ['When', whenLabel(from.localStartDatetime), whenLabel(newBooking.localStartsAt)],
  ];

  return (
    <div role="status" aria-live="polite" className="flex min-h-0 flex-1 flex-col">
      <div
        role="status"
        aria-live="polite"
        className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-6"
      >
        <Link
          to={`/admin/bookings/${newBooking.id}`}
          className="flex items-center gap-3 rounded-xl border border-moss-400 bg-moss-50/60 px-4 py-3 dark:border-moss-700 dark:bg-moss-900/10"
        >
          <div role="status" aria-live="polite" className="min-w-0 flex-1">
            <div
              role="status"
              aria-live="polite"
              className="text-[10px] font-extrabold uppercase tracking-wider text-moss-700 dark:text-moss-300"
            >
              New booking
            </div>
            {/* Always a real code. A successor is minted here rather than
                imported, so unlike the refund drawer's header this never has to
                fall back to an unreadable 36-character UUID. */}
            <div
              role="status"
              aria-live="polite"
              className="font-mono text-base font-bold tracking-wide text-moss-800 dark:text-moss-200"
            >
              {newBooking.confirmationCode}
            </div>
          </div>
          <ChevronRight size={16} className="flex-shrink-0 text-moss-600" />
        </Link>

        <div
          role="status"
          aria-live="polite"
          className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-700"
        >
          {changes.map(([label, was, now]) => changeRow(label, was, now))}
        </div>

        <p className="text-xs leading-relaxed text-stone-500 dark:text-stone-400">
          {booking.money.ticketsCount} {booking.money.ticketsCount === 1 ? 'seat' : 'seats'}{' '}
          reopened on {from.name ?? 'the old workshop'} · {from.venue.name}. This booking is now
          closed.
          {result.emailSent
            ? ` ${customerDisplayName(booking)} has been emailed about the new workshop.`
            : ` We could not email ${customerDisplayName(booking)} — the new booking has been flagged for review, so tell them yourself.`}
        </p>
      </div>
      <div
        role="status"
        aria-live="polite"
        className="flex justify-end border-t border-stone-200 px-6 py-4 dark:border-stone-700"
      >
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg bg-moss-600 px-4 py-2 text-sm font-semibold text-white hover:bg-moss-700"
        >
          Done
        </button>
      </div>
    </div>
  );
}
